import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { ordersAPI } from '../../utils/api';
import { LoadingSpinner, StatusBadge } from '../../components/common';

const STEPS = ['placed', 'inprogress', 'shipped', 'delivered'];

const OrderPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const justPlaced = location.state?.justPlaced || new URLSearchParams(location.search).get('justPlaced') === 'true';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersAPI.getById(id)
      .then(r => setOrder(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (!order) return <div className="container"><p style={{ padding: '2rem' }}>Order not found.</p></div>;

  const addr = typeof order.shipping_address === 'string' ? JSON.parse(order.shipping_address) : order.shipping_address;
  const stepIndex = STEPS.indexOf(order.status);

  return (
    <div className="container" style={{ padding: '1.5rem 1rem', maxWidth: 800 }}>

      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#2563eb', fontWeight: 600, fontSize: '0.88rem',
          marginBottom: '1rem', padding: '0.25rem 0',
        }}
      >
        ← Back to Orders
      </button>

      {/* Amazon-style success banner (shown right after checkout) */}
      {justPlaced && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
          padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
          display: 'flex', alignItems: 'flex-start', gap: '1rem',
        }}>
          <div style={{ fontSize: '2rem', lineHeight: 1 }}>&#x2705;</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ color: '#15803d', fontWeight: 800, fontSize: '1.25rem', margin: '0 0 0.25rem' }}>
              Order placed, Thank you!
            </h2>
            <p style={{ color: '#166534', margin: '0 0 0.5rem', fontSize: '0.93rem' }}>
              Confirmation will be sent to your email. Your order number is{' '}
              <strong style={{ color: '#166534' }}>#{order.order_number}</strong>.
            </p>
            <p style={{ color: '#166534', margin: 0, fontSize: '0.87rem' }}>
              Total charged: <strong>${parseFloat(order.total).toFixed(2)}</strong>
              {' · '}Payment: {order.payment_method === 'Braintree' ? 'Online' : order.payment_method || 'Online'}
            </p>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <Link to="/kits" style={{ fontSize: '0.85rem', color: '#15803d', fontWeight: 600, textDecoration: 'underline' }}>
              Continue Shopping
            </Link>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 800 }}>Order #{order.order_number}</h1>
        <StatusBadge status={order.status} />
      </div>

      {/* Tracker */}
      {order.status !== 'cancelled' && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 16, left: '8%', right: '8%', height: 3, background: '#e5e7eb', zIndex: 0 }} />
            <div style={{ position: 'absolute', top: 16, left: '8%', width: `${Math.max(0, stepIndex / (STEPS.length - 1)) * 84}%`, height: 3, background: '#2563eb', zIndex: 1, transition: 'width 0.5s' }} />
            {STEPS.map((step, i) => (
              <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, width: '25%' }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: i <= stepIndex ? '#2563eb' : '#e5e7eb',
                  color: i <= stepIndex ? '#fff' : '#9ca3af',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem',
                  border: i === stepIndex ? '3px solid #1d4ed8' : 'none',
                  transition: 'background 0.3s',
                }}>
                  {i < stepIndex ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: '0.72rem', marginTop: '0.4rem', fontWeight: i <= stepIndex ? 700 : 400, textTransform: 'capitalize', textAlign: 'center' }}>
                  {step}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Items */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontWeight: 700, marginBottom: '0.75rem' }}>Items Ordered</h2>
        {order.items?.map(item => (
          <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.88rem' }}>
            <span>{item.name} × {item.qty}</span>
            <span style={{ fontWeight: 700 }}>${parseFloat(item.total_price).toFixed(2)}</span>
          </div>
        ))}
        <div style={{ marginTop: '0.75rem', fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>${parseFloat(order.subtotal).toFixed(2)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Shipping</span><span>${parseFloat(order.shipping_fee).toFixed(2)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tax</span><span>${parseFloat(order.tax).toFixed(2)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1rem', borderTop: '2px solid #e5e7eb', paddingTop: '0.4rem', marginTop: '0.2rem' }}>
            <span>Total</span><span>${parseFloat(order.total).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Address */}
      {addr && (
        <div className="card">
          <h2 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Delivery Address</h2>
          <p style={{ fontSize: '0.88rem', lineHeight: 1.7 }}>
            {addr.full_name}<br />{addr.address_line1}{addr.address_line2 ? ', ' + addr.address_line2 : ''}<br />
            {addr.city}{addr.state ? ', ' + addr.state : ''} {addr.pincode}
          </p>
        </div>
      )}
    </div>
  );
};

export default OrderPage;
